// assets/AllCourses/Exercises/FillInTheBlanksExercise.jsx

import React, { useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function FillInTheBlanksExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= QUESTIONS ================= */
  const questions = Array.isArray(exercise.fillInTheBlanksQuestions)
    ? exercise.fillInTheBlanksQuestions.map((q) => ({
        ...q,
        type: "fill",
      }))
    : [];

  /* ================= SAFE EXTERNAL ================= */
  const safeExternalAnswers =
    typeof userAnswer === "object" ? userAnswer : {};

  /* ================= ENGINE ================= */
  const { answers, setAnswer, getInputState } = useExerciseEngine({
    questions,
    showResult,
    externalAnswers: safeExternalAnswers,
  });

  /* ================= SYNC TO PARENT ================= */
  const prevRef = useRef({});

  useEffect(() => {
    if (isPreview) return;
  if (!Object.keys(answers).length) return;

    const isSame =
      JSON.stringify(prevRef.current) === JSON.stringify(answers);

    if (isSame) return;

    prevRef.current = answers;

    onComplete({
      answer: answers,
    });
  }, [answers]);

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) {
      onUpdate("fillInTheBlanksQuestions", next);
    }
  };

  const updateQuestionField = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || {
      sentence: "",
      answers: [],
      requireCapitalization: false,
    };

    let updated = { ...current, [field]: value };

    if (field === "sentence") {
      const blanks = (value.match(/_+/g) || []).length;
      updated.answers = Array.from(
        { length: blanks },
        (_, i) => current.answers?.[i] || ""
      );
    }

    next[qIdx] = updated;
    updateQuestions(next);
  };

  /* ================= EDIT UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => {
          const blanks = (q.sentence.match(/_+/g) || []).length;

          return (
            <Card key={qIdx} className="p-6">
              <div className="space-y-6">
                <TextareaField
                  label={`Sentence ${qIdx + 1}`}
                  value={q.sentence || ""}
                  onChange={(v) =>
                    updateQuestionField(qIdx, "sentence", v)
                  }
                  placeholder="The ___ is shining."
                  rows={4}
                  required
                />

                {Array.from({ length: blanks }).map((_, i) => (
                  <StringField
                    key={i}
                    label={`Blank ${i + 1}`}
                    value={q.answers[i] || ""}
                    onChange={(v) => {
                      const next = [...questions];
                      next[qIdx].answers[i] = v;
                      updateQuestions(next);
                    }}
                  />
                ))}

                <CapitalizationRule
                  checked={!!q.requireCapitalization}
                  onChange={(c) =>
                    updateQuestionField(
                      qIdx,
                      "requireCapitalization",
                      c
                    )
                  }
                />

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() =>
                    updateQuestions(
                      questions.filter((_, i) => i !== qIdx)
                    )
                  }
                >
                  Remove Question
                </Button>
              </div>
            </Card>
          );
        })}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                sentence: "",
                answers: [],
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No fill-in-the-blanks questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Fill in the Blanks</CardTitle>
        <CardDescription>
          Type correct words to complete sentences
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const parts = q.sentence.split(/(_+)/g).filter(Boolean);

          let blankIndex = 0;

          return (
            <div
              key={qIdx}
              className="rounded-xl border bg-muted/40 p-6 flex flex-wrap gap-3"
            >
              {parts.map((part, i) => {
                if (/_+/.test(part)) {
                  const bIdx = blankIndex++;
                  const key = `${qIdx}-${bIdx}`; // stable key

                  const value = answers[key] || "";

                  const state = getInputState(
                    value,
                    q.answers[bIdx]
                  );

                  return (
                    <Input
                      key={i}
                      value={value}
                      onChange={(e) => {
                        setAnswer(key, e.target.value);
                      }}
                      disabled={showResult}
                      className={cn(
                        "w-32 text-center font-medium border transition",
                        state.className
                      )}
                    />
                  );
                }

                return <span key={i}>{part}</span>;
              })}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}